/**
 * EmailInboxView — the single inbox for mailbox-synced email.
 *
 * WHY THIS EXISTS. `EmailNeedsAssignment` shows only the unassigned queue, and it is
 * mounted inside `UnifiedCommunicationInbox`, which is a REFERRAL inbox reached through
 * Case Workspace after picking a referral. A case manager working the member Inbox never
 * encounters it. Once a message is assigned it also disappears entirely — there was no
 * way to see a member's synced mail, search it, or find something assigned by mistake.
 *
 * SHAPE. Filter rail (left) · message list (middle) · reading pane (right). The list
 * carries snippets; the pane fetches the full body, which is the point of syncing bodies
 * at all. Assignment offers ranked suggestions instead of a blank search box, because
 * only ~30% of LCC members have an email on file — manual assignment is the primary path
 * and the queue runs to dozens.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Mail,
  RefreshCw,
  Search,
  UserPlus,
  UserMinus,
  Paperclip,
  ExternalLink,
  Inbox as InboxIcon,
  Send,
  CheckCircle2,
  Loader2,
  Reply,
  Archive,
  ArchiveRestore,
  Trash2,
  MailOpen,
  Repeat2,
} from 'lucide-react';
import { OpenInNewMenu } from '@/components/messaging/popout/OpenInNewMenu';
import { apiClient } from '@/services/apiClient';
import {
  inboxEmailAPI,
  type InboxEmail,
  type InboxEmailDetail,
  type MailboxStatus,
  type MemberSuggestion,
  type EmailTemplate,
} from '@/services/api/inboxEmail';
import { oauthAPI } from '@/services/api/oauth';
import { useFormattedDate } from '@/hooks/useUserTimezone';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { toast } from '@/components/ui/use-toast';
import { SwipeRow } from '@/components/messaging/swipe/SwipeRow';
import { swipeSides, useSwipeSettings } from '@/components/messaging/swipe/swipeSettings';
import { EmailHtmlBody } from '@/components/inbox/EmailHtmlBody';

type Filter = 'all' | 'unassigned' | 'assigned';

interface MemberResult {
  id: number;
  first_name?: string;
  last_name?: string;
  email?: string;
}

const CONFIDENCE_STYLE: Record<MemberSuggestion['confidence'], string> = {
  high: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  low: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

export const EmailInboxView: React.FC<{
  memberId?: number;
  /** Show "open in new tab / window". Off where the embedding page already has one. */
  showPopoutControl?: boolean;
}> = ({ memberId, showPopoutControl = true }) => {
  const { fmt } = useFormattedDate();
  const { t } = useAppTranslation();
  const swipeSettings = useSwipeSettings();

  const [mailbox, setMailbox] = useState<MailboxStatus | null>(null);
  const [filter, setFilter] = useState<Filter>(memberId ? 'all' : 'unassigned');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<InboxEmail[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<InboxEmailDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Assignment panel
  const [suggestions, setSuggestions] = useState<MemberSuggestion[]>([]);
  const [memberQuery, setMemberQuery] = useState('');
  const [memberResults, setMemberResults] = useState<MemberResult[]>([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // Bulk selection
  const [checked, setChecked] = useState<Set<number>>(new Set());

  // Reply composer
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [replySubject, setReplySubject] = useState('');
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [templateId, setTemplateId] = useState<number | ''>('');

  // Reassign: an already-linked message can be pointed at a different member without
  // unassigning first — the backend cleans up the previous member's record.
  const [reassigning, setReassigning] = useState(false);

  // active hides archived; archived shows only those. Deleted is never listed.
  const [view, setView] = useState<'active' | 'archived'>('active');

  // Debounce the free-text filter: it drives a server query on every keystroke otherwise.
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await inboxEmailAPI.list({
        assigned: filter,
        view,
        member_id: memberId,
        q: debouncedSearch.length >= 2 ? debouncedSearch : undefined,
        limit: 100,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filter, view, memberId, debouncedSearch]);

  useEffect(() => {
    inboxEmailAPI.status().then(setMailbox).catch(() => setMailbox(null));
    inboxEmailAPI.templates().then(setTemplates).catch(() => setTemplates([]));
  }, []);

  useEffect(() => {
    if (mailbox?.connected) load();
    else setLoading(false);
  }, [mailbox?.connected, load]);

  // Reading pane + suggestions for the selected message.
  useEffect(() => {
    if (selectedId == null) {
      setDetail(null);
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    inboxEmailAPI
      .get(selectedId)
      .then((r) => {
        if (!cancelled) setDetail(r.message);
      })
      .catch(() => {
        if (!cancelled) setDetail(null);
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    inboxEmailAPI
      .suggestions(selectedId)
      .then((r) => {
        if (!cancelled) setSuggestions(r.suggestions);
      })
      .catch(() => {
        if (!cancelled) setSuggestions([]);
      });
    return () => {
      cancelled = true;
    };
    setReplyOpen(false);
    setReplyBody('');
    setReplySubject('');
    setTemplateId('');
    setReassigning(false);
  }, [selectedId]);

  const searchMembers = async (q: string) => {
    setMemberQuery(q);
    if (q.trim().length < 2) {
      setMemberResults([]);
      return;
    }
    try {
      const res = await apiClient.get('/api/members/search/unified', { params: { q, limit: 15 } });
      setMemberResults(res.data?.members || []);
    } catch {
      setMemberResults([]);
    }
  };

  const assign = async (messageId: number, member: number, assignThread: boolean) => {
    setBusy(true);
    try {
      const r = await inboxEmailAPI.assign(messageId, member, {
        assign_thread: assignThread,
        remember_address: true,
      });
      const extra = r.thread_messages_assigned
        ? ` and ${r.thread_messages_assigned} more in the thread`
        : '';
      const learned = r.remembered_address ? ` Future mail from ${r.remembered_address} will link automatically.` : '';
      setNotice(`Assigned${extra}.${learned}`);
      setMemberQuery('');
      setMemberResults([]);
      await load();
      setSelectedId(null);
    } catch (e: any) {
      setNotice(e?.response?.data?.detail || 'Could not assign this email.');
    } finally {
      setBusy(false);
    }
  };

  const unassign = async (messageId: number) => {
    if (
      !window.confirm(
        'Remove this email from the member record? It goes back to the unassigned queue and is removed from their communication history.',
      )
    )
      return;
    setBusy(true);
    try {
      await inboxEmailAPI.unassign(messageId);
      setNotice('Unassigned and returned to the queue.');
      await load();
      setSelectedId(null);
    } catch (e: any) {
      setNotice(e?.response?.data?.detail || 'Could not unassign this email.');
    } finally {
      setBusy(false);
    }
  };

  const bulkAssignTo = async (member: number) => {
    const ids = Array.from(checked);
    if (!ids.length) return;
    setBusy(true);
    try {
      const r = await inboxEmailAPI.bulkAssign(ids.map((id) => ({ message_id: id, member_id: member })));
      const failed = r.failed?.length || 0;
      setNotice(
        `Assigned ${ids.length - failed} of ${ids.length}${failed ? ` — ${failed} failed.` : '.'}`,
      );
      setChecked(new Set());
      setMemberQuery('');
      setMemberResults([]);
      await load();
    } catch (e: any) {
      setNotice(e?.response?.data?.detail || 'Bulk assign failed.');
    } finally {
      setBusy(false);
    }
  };


  /** Runs a row action; true when it worked. */
  const runAction = async (fn: () => Promise<unknown>, done: string): Promise<boolean> => {
    setBusy(true);
    try {
      await fn();
      setNotice(done);
      await load();
      return true;
    } catch (e: any) {
      setNotice(e?.response?.data?.detail || 'That did not work. Please try again.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const toggleRead = (m: InboxEmail) =>
    runAction(() => inboxEmailAPI.markRead(m.id, !m.is_read), m.is_read ? 'Marked unread.' : 'Marked read.');

  const toggleArchive = async (m: InboxEmail) => {
    const archiving = !m.archived_at;
    const ok = await runAction(
      () => inboxEmailAPI.archive(m.id, archiving),
      archiving ? 'Archived. It stays in your Outlook mailbox.' : 'Moved back to the inbox.',
    );
    // TRUS-759: a swipe archives in one motion, so it can be undone.
    if (ok && archiving) {
      toast({
        title: t('inbox.email.archived', 'Email archived'),
        action: {
          label: t('inbox.email.undo', 'Undo'),
          onClick: () => void runAction(() => inboxEmailAPI.archive(m.id, false), 'Moved back to the inbox.'),
        },
      });
    }
  };

  const removeMessage = async (m: InboxEmail) => {
    if (
      !window.confirm(
        'Remove this email from WeaveIn?\n\nIt stays in your Outlook mailbox — this only removes it here, and takes it off any member record it was filed against.',
      )
    )
      return;
    await runAction(() => inboxEmailAPI.remove(m.id), 'Removed from WeaveIn.');
    setSelectedId(null);
  };

  const applyTemplate = (id: number | '') => {
    setTemplateId(id);
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    // Show the template as authored. {variables} are resolved server-side against the
    // linked member at send time, so the user sees the same placeholders a campaign uses.
    if (t.body) setReplyBody(t.body);
    if (t.subject) setReplySubject(t.subject);
  };

  const sendReply = async () => {
    if (!selectedId || !replyBody.trim()) return;
    setBusy(true);
    try {
      const r = await inboxEmailAPI.reply(selectedId, {
        body_html: replyBody,
        subject: replySubject.trim() || undefined,
        template_id: templateId === '' ? undefined : templateId,
      });
      setNotice(
        r.threaded
          ? `Replied to ${r.to} from ${r.from_email} — kept in the same thread.`
          : `Sent to ${r.to} from ${r.from_email}.`,
      );
      setReplyOpen(false);
      setReplyBody('');
      setReplySubject('');
      setTemplateId('');
      await load();
    } catch (e: any) {
      setNotice(e?.response?.data?.detail || 'Could not send the reply.');
    } finally {
      setBusy(false);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      await inboxEmailAPI.syncNow();
      // The worker runs out of band; give it a moment before re-reading.
      setTimeout(load, 3000);
    } catch (e: any) {
      setNotice(e?.response?.data?.detail || 'Sync is unavailable right now.');
    } finally {
      setSyncing(false);
    }
  };

  const connectOutlook = async () => {
    setConnecting(true);
    try {
      // Email-only consent: 4 scopes rather than 7. Most Microsoft 365 tenants need an
      // admin to approve the app, and over-asking is why that gets refused.
      const { auth_url } = await oauthAPI.microsoft.authorize(window.location.pathname, 'email');
      if (auth_url) {
        window.location.href = auth_url;
        return;
      }
      setNotice('Could not start the Microsoft connection.');
    } catch (e: any) {
      setNotice(e?.response?.data?.detail || 'Could not start the Microsoft connection.');
    } finally {
      setConnecting(false);
    }
  };

  const toggleCheck = (id: number) => {
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const counts = useMemo(
    () => ({
      unassigned: items.filter((i) => !i.member_id).length,
      assigned: items.filter((i) => i.member_id).length,
    }),
    [items],
  );

  // ---------------------------------------------------------------- not connected
  if (!mailbox?.connected) {
    return (
      <div className="p-10 text-center">
        <Mail className="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">No mailbox connected</p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md mx-auto">
          Connect Outlook to see your member email here, assign it to the right person, and reply
          from your own address.
        </p>
        <button
          onClick={connectOutlook}
          disabled={connecting}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 text-sm font-medium"
        >
          {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
          {connecting ? 'Opening Microsoft…' : 'Connect Outlook'}
        </button>
        <p className="text-xs text-gray-400 mt-3 max-w-md mx-auto">
          Asks only for mail access (read and send). Calendar and contacts are requested
          separately from Settings → Connected Services.
        </p>
      </div>
    );
  }

  const selected = items.find((i) => i.id === selectedId) || null;

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-1">
          {(['unassigned', 'assigned', 'all'] as Filter[]).map((f) => (
            <button
              key={f}
              onClick={() => {
                setFilter(f);
                setSelectedId(null);
                setChecked(new Set());
              }}
              className={`px-2.5 py-1 text-xs font-medium rounded capitalize ${
                filter === f
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {f === 'unassigned' ? 'Needs assignment' : f}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1.5 flex-1 min-w-[12rem]">
          <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search subject or sender…"
            className="flex-1 text-sm bg-transparent border-b border-transparent focus:border-gray-300 focus:outline-none py-0.5"
          />
        </div>

        <div className="flex items-center gap-1">
          {(['active', 'archived'] as const).map((v) => (
            <button
              key={v}
              onClick={() => { setView(v); setSelectedId(null); setChecked(new Set()); }}
              className={`px-2 py-1 text-xs font-medium rounded ${
                view === v
                  ? 'bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {v === 'active' ? 'Inbox' : 'Archived'}
            </button>
          ))}
        </div>

        <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
          {mailbox.email} · {total} message{total === 1 ? '' : 's'}
        </span>

        <button
          onClick={syncNow}
          disabled={syncing}
          className="flex items-center gap-1 text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} />
          Sync now
        </button>
        {/* A member's mail (memberId) is a profile panel, not the Mailbox — nothing to pop out. */}
        {showPopoutControl && memberId == null && (
          <OpenInNewMenu getTabs={() => ({ tabs: [{ kind: 'mailbox' }] })} />
        )}
      </div>

      {notice && (
        <div className="px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-sm text-blue-800 dark:text-blue-200 flex items-start justify-between gap-3">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} className="text-xs underline shrink-0">
            dismiss
          </button>
        </div>
      )}

      {checked.size > 0 && (
        <div className="px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-amber-900 dark:text-amber-200">
              {checked.size} selected — assign all to:
            </span>
            <input
              value={memberQuery}
              onChange={(e) => searchMembers(e.target.value)}
              placeholder="Search member…"
              className="text-sm border border-amber-300 dark:border-amber-700 rounded px-2 py-1 dark:bg-gray-800"
            />
            <button onClick={() => setChecked(new Set())} className="text-xs underline text-amber-800 dark:text-amber-300">
              clear
            </button>
          </div>
          {memberResults.length > 0 && (
            <div className="mt-2 max-h-40 overflow-y-auto border border-amber-200 dark:border-amber-800 rounded divide-y bg-white dark:bg-gray-800">
              {memberResults.map((m) => (
                <button
                  key={m.id}
                  disabled={busy}
                  onClick={() => bulkAssignTo(m.id)}
                  className="w-full text-left px-2 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex justify-between gap-2"
                >
                  <span>{[m.first_name, m.last_name].filter(Boolean).join(' ') || `Member #${m.id}`}</span>
                  <span className="text-xs text-gray-400">{m.email}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex-1 flex min-h-0">
        {/* Message list */}
        <div className={`${selectedId ? 'w-2/5' : 'w-full'} border-r border-gray-200 dark:border-gray-700 overflow-y-auto`}>
          {loading ? (
            <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center">
              <CheckCircle2 className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {filter === 'unassigned' ? 'Nothing waiting to be assigned.' : 'No email here yet.'}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {items.map((m) => (
                <li key={m.id}>
                  {/* TRUS-759/761: swipe to mark read / unread or archive (with Undo), per
                      the user's swipe settings. The row's buttons do the same. */}
                  <SwipeRow
                    disabled={busy}
                    {...swipeSides(swipeSettings.email, {
                      read: {
                        label: m.is_read ? t('inbox.email.markUnread', 'Mark unread') : t('inbox.email.markRead', 'Mark read'),
                        icon: m.is_read ? Mail : MailOpen,
                        tone: 'read' as const,
                        onCommit: () => void toggleRead(m),
                      },
                      archive: {
                        label: m.archived_at ? t('inbox.email.unarchive', 'Move to inbox') : t('inbox.email.archive', 'Archive'),
                        icon: m.archived_at ? ArchiveRestore : Archive,
                        tone: 'archive' as const,
                        onCommit: () => void toggleArchive(m),
                      },
                    })}
                    contentClassName="bg-white dark:bg-gray-800"
                    outerProps={{ 'data-testid': `email-swipe-${m.id}` }}
                  >
                  <div className={`flex items-start gap-2 px-3 py-2.5 ${selectedId === m.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                    {!m.member_id && (
                      <input
                        type="checkbox"
                        checked={checked.has(m.id)}
                        onChange={() => toggleCheck(m.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1 shrink-0"
                        aria-label={`Select ${m.subject || 'email'}`}
                      />
                    )}
                    <button onClick={() => setSelectedId(m.id)} className="flex-1 text-left min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm truncate ${m.is_read ? 'text-gray-700 dark:text-gray-300' : 'font-semibold text-gray-900 dark:text-gray-100'}`}>
                          {m.from_name || m.from_email || 'Unknown sender'}
                        </span>
                        {m.folder === 'sentitems' && (
                          // Lucide icons do not take `title`; the wrapper carries the
                          // tooltip and gives screen readers something to announce.
                          <span title="Sent from your mailbox" className="shrink-0" aria-label="Sent">
                            <Send className="w-3 h-3 text-gray-400" />
                          </span>
                        )}
                        {m.has_attachments && <Paperclip className="w-3 h-3 text-gray-400 shrink-0" />}
                      </div>
                      <p className="text-sm text-gray-800 dark:text-gray-200 truncate">{m.subject || '(no subject)'}</p>
                      <p className="text-xs text-gray-400 truncate">{m.snippet}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[11px] text-gray-400 tabular-nums">
                          {m.sent_at ? fmt.dateTime(m.sent_at) : ''}
                        </span>
                        {m.member_id ? (
                          <span className="text-[11px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
                            linked
                          </span>
                        ) : (
                          <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                            unassigned
                          </span>
                        )}
                      </div>
                    </button>
                    <div className="flex shrink-0 items-center gap-0.5">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleRead(m); }}
                        disabled={busy}
                        title={m.is_read ? 'Mark unread' : 'Mark read'}
                        aria-label={m.is_read ? 'Mark unread' : 'Mark read'}
                        className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40"
                      >
                        {m.is_read ? <Mail className="w-3.5 h-3.5" /> : <MailOpen className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleArchive(m); }}
                        disabled={busy}
                        title={m.archived_at ? 'Move back to inbox' : 'Archive'}
                        aria-label={m.archived_at ? 'Move back to inbox' : 'Archive'}
                        className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40"
                      >
                        {m.archived_at ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); removeMessage(m); }}
                        disabled={busy}
                        title="Remove from WeaveIn (stays in Outlook)"
                        aria-label="Remove from WeaveIn"
                        className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  </SwipeRow>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Reading pane */}
        {selectedId && (
          <div className="flex-1 overflow-y-auto bg-white dark:bg-gray-800">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                    {detail?.subject || selected?.subject || '(no subject)'}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                    From {detail?.from_email || selected?.from_email} · to {detail?.to_emails || selected?.to_emails}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {(detail?.web_link || selected?.web_link) && (
                    <a
                      href={(detail?.web_link || selected?.web_link) as string}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs flex items-center gap-1 text-blue-600 hover:underline"
                    >
                      <ExternalLink className="w-3 h-3" /> Outlook
                    </a>
                  )}
                  <button
                    onClick={() => { setReplyOpen((v) => !v); if (!replySubject) setReplySubject(`Re: ${(detail?.subject || selected?.subject || '').replace(/^Re:\s*/i, '')}`); }}
                    className="flex items-center gap-1 text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    <Reply className="w-3 h-3" /> Reply
                  </button>
                  <button onClick={() => setSelectedId(null)} className="text-xs text-gray-400 hover:text-gray-600">
                    close
                  </button>
                </div>
              </div>
            </div>

            <div className="p-4">
              {detailLoading ? (
                <p className="text-sm text-gray-400">Loading message…</p>
              ) : detail?.body_html ? (
                // Untrusted mail: sanitized and shown in a sandboxed frame, never in our DOM.
                <EmailHtmlBody html={detail.body_html} title={detail.subject || 'Email message'} />
              ) : (
                <pre className="text-sm whitespace-pre-wrap font-sans text-gray-800 dark:text-gray-200">
                  {detail?.body_text || selected?.snippet || 'No content.'}
                </pre>
              )}
            </div>

            {/* Reply composer */}
            {replyOpen && (
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      From <span className="font-medium">{mailbox.email}</span>
                    </span>
                    {templates.length > 0 && (
                      <select
                        value={templateId}
                        onChange={(e) => applyTemplate(e.target.value === '' ? '' : Number(e.target.value))}
                        className="text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded px-2 py-1"
                      >
                        <option value="">No template</option>
                        {templates.map((t) => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  <input
                    value={replySubject}
                    onChange={(e) => setReplySubject(e.target.value)}
                    placeholder="Subject"
                    className="text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded px-2 py-1.5"
                  />
                  <textarea
                    value={replyBody}
                    onChange={(e) => setReplyBody(e.target.value)}
                    rows={6}
                    placeholder="Write your reply…"
                    className="text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded px-2 py-1.5 resize-y"
                  />
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-[11px] text-gray-400">
                      {selected?.member_id
                        ? 'Placeholders like {first_name} fill in from the linked member when it sends.'
                        : 'Not linked to a member yet, so placeholders will not fill in.'}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setReplyOpen(false)}
                        className="text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={sendReply}
                        disabled={busy || !replyBody.trim()}
                        className="flex items-center gap-1.5 text-sm px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        Send reply
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Assignment */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              {selected?.member_id && !reassigning ? (
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => { setReassigning(true); setMemberQuery(''); setMemberResults([]); }}
                    disabled={busy}
                    className="flex items-center gap-1.5 text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60"
                  >
                    <Repeat2 className="w-3.5 h-3.5" />
                    File under a different member
                  </button>
                  <button
                    onClick={() => unassign(selected.id)}
                    disabled={busy}
                    className="flex items-center gap-1.5 text-sm px-3 py-1.5 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-60"
                  >
                    <UserMinus className="w-3.5 h-3.5" />
                    Remove from member record
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      {reassigning ? 'File under a different member' : 'Assign to member'}
                    </p>
                    {reassigning && (
                      <button onClick={() => setReassigning(false)} className="text-xs underline text-gray-400">
                        cancel
                      </button>
                    )}
                  </div>
                  {reassigning && (
                    <p className="text-[11px] text-gray-400 mb-2">
                      Choosing someone else moves the email off the current member&rsquo;s record.
                    </p>
                  )}
                  {suggestions.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {suggestions.map((s) => (
                        <button
                          key={s.member_id}
                          disabled={busy}
                          onClick={() => assign(selectedId, s.member_id, true)}
                          className="flex items-center gap-1.5 text-sm px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60"
                        >
                          <UserPlus className="w-3.5 h-3.5 text-gray-400" />
                          <span>{s.name || `Member #${s.member_id}`}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${CONFIDENCE_STYLE[s.confidence]}`}>
                            {s.reason}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Search className="w-3.5 h-3.5 text-gray-400" />
                    <input
                      value={memberQuery}
                      onChange={(e) => searchMembers(e.target.value)}
                      placeholder="Search by name or email…"
                      className="flex-1 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded px-2 py-1"
                    />
                  </div>
                  {memberResults.length > 0 && (
                    <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded divide-y">
                      {memberResults.map((m) => (
                        <button
                          key={m.id}
                          disabled={busy}
                          onClick={() => assign(selectedId, m.id, true)}
                          className="w-full text-left px-2 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex justify-between gap-2"
                        >
                          <span>{[m.first_name, m.last_name].filter(Boolean).join(' ') || `Member #${m.id}`}</span>
                          <span className="text-xs text-gray-400">{m.email}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="text-[11px] text-gray-400 mt-2">
                    Assigns the whole thread and remembers this address, so future mail from it
                    links automatically.
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer counts — cheap orientation without another request. */}
      <div className="px-4 py-1.5 border-t border-gray-200 dark:border-gray-700 text-[11px] text-gray-400 flex items-center gap-3">
        <InboxIcon className="w-3 h-3" />
        <span className="tabular-nums">{counts.unassigned} unassigned</span>
        <span className="tabular-nums">{counts.assigned} linked</span>
      </div>
    </div>
  );
};

export default EmailInboxView;
